/**
 * Eventos View - Vista de gestión de eventos deportivos
 * 
 * Funcionalidades:
 * - Calendario mensual con eventos
 * - Lista de eventos del día seleccionado
 * - Creación y edición de eventos
 * - Filtrado por fecha
 * - Visualización de entrenamientos y torneos
 */

import React, { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import {
  listEventosActivosFechasTipos,
  listEventosFiltered,
  createEvento,
  updateEvento,
  deleteEvento,
  TIPOS_EVENTO,
  ESTADOS_EVENTO,
} from "../../services/eventosService";
import { listGrupos } from "../../services/gruposService";
import { getConvocatoriasByEvento } from "../../services/convocatoriaService";
import { ModalEvento } from "../../modals/ModalEvento";
import Modal from "antd/es/modal/Modal";
import Popover from "antd/es/popover";
import { ReloadOutlined } from "@ant-design/icons";
import {
  getPartidos,
  createPartido,
  updatePartido,
} from "../../services/partidosServices";

/* ===== UI Helpers ===== */
function clsx(...c) {
  return c.filter(Boolean).join(" ");
}

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
    purple: "bg-purple-50 text-purple-700 border-purple-200",
  };
  
  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-xl border text-xs font-medium px-2.5 py-1",
        variants[variant]
      )}
    >
      {children}
    </span>
  );
}

/* ===== Helpers de fecha ===== */
function getMonthName(monthIndex) {
  const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];
  return months[monthIndex];
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatTime(isoString) {
  if (!isoString) return "";

  // Extrae HH:MM directamente sin crear Date
  const match = isoString.match(/T(\d{2}):(\d{2})/);
  if (!match) return "";

  const [, hours, minutes] = match;
  return `${hours}:${minutes}`;
}

function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  
  // Si date1 es string ISO, extraer solo la fecha
  if (typeof date1 === 'string') {
    const d1 = new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);
    return (
      d1.getUTCFullYear() === d2.getFullYear() &&
      d1.getUTCMonth() === d2.getMonth() &&
      d1.getUTCDate() === d2.getDate()
    );
  }
  
  // Si ambos son Date
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);
  
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

/* ===== Íconos ===== */
function ChevronLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function PlusIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function UsersIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function InboxIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

/* ===== Componentes auxiliares ===== */

// Badge para tipo de evento
function TipoBadge({ tipo }) {
  if (tipo === TIPOS_EVENTO.ENTRENAMIENTO) {
    return <Badge variant="info">🏃 Entrenamiento</Badge>;
  }
  if (tipo === TIPOS_EVENTO.TORNEO) {
    return <Badge variant="purple">🏆 Torneo</Badge>;
  }
  return <Badge variant="default">{tipo}</Badge>;
}

// Badge para estado de evento
function EstadoBadge({ estado }) {
  if (estado === ESTADOS_EVENTO.PROGRAMADO) {
    return <Badge variant="info">📅 Programado</Badge>;
  }
  if (estado === ESTADOS_EVENTO.EN_CURSO) {
    return <Badge variant="success">▶️ En curso</Badge>;
  }
  if (estado === ESTADOS_EVENTO.FINALIZADO) {
    return <Badge variant="default">✅ Finalizado</Badge>;
  }
  if (estado === ESTADOS_EVENTO.CANCELADO) {
    return <Badge variant="danger">❌ Cancelado</Badge>;
  }
  return <Badge variant="default">{estado}</Badge>;
}

/* ===== Componente principal ===== */
export default function Eventos() {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Estados de datos
  const [eventosCalendario, setEventosCalendario] = useState([]);
  const [eventosDia, setEventosDia] = useState([]);
  const [grupos, setGrupos] = useState([]);

  // Estados de UI
  const [loadingCalendario, setLoadingCalendario] = useState(true);
  const [loadingEventosDia, setLoadingEventosDia] = useState(false);

  // Modal crear/editar
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modal eliminar
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [targetDelete, setTargetDelete] = useState(null);

  // Modal convocatoria
  const [showConvocatoria, setShowConvocatoria] = useState(false);
  const [convocatorias, setConvocatorias] = useState([]);
  const [loadingConvocatorias, setLoadingConvocatorias] = useState(false);
  const [selectedEventoConv, setSelectedEventoConv] = useState(null);
  // Partidos (global for modal)
  const [partidos, setPartidos] = useState([]);
  const [loadingPartidos, setLoadingPartidos] = useState(false);
  // Popover form state
  const [popoverVisibleFor, setPopoverVisibleFor] = useState(null); // convocatoriaId currently opening form for
  const [editingPartido, setEditingPartido] = useState(null);
  const [partidoForm, setPartidoForm] = useState({
    equipo_local: "",
    equipo_visitante: "",
    result_local: "",
    result_visitante: "",
    fecha_partido: "",
    hora_partido: "",
    cancha: "",
    estado: "programado",
  });
  // saving state for partido form and accordion state for convocatorias
  const [savingPartido, setSavingPartido] = useState(false);
  const [expandedConvocatorias, setExpandedConvocatorias] = useState({});

  // ============================================================================
  // CARGA DE DATOS
  // ============================================================================

  // Cargar eventos del calendario (solo fechas y tipos)
  const loadEventosCalendario = useCallback(async () => {
    try {
      setLoadingCalendario(true);
      const data = await listEventosActivosFechasTipos();
      setEventosCalendario(data);
    } catch (err) {
      console.error("Error loading eventos calendario:", err);
      toast.error(err?.message || "Error al cargar eventos del calendario");
    } finally {
      setLoadingCalendario(false);
    }
  }, []);

  // Cargar eventos del día seleccionado
  const loadEventosDia = useCallback(async (date) => {
    try {
      setLoadingEventosDia(true);
      const fechaStr = formatDate(date);
      const result = await listEventosFiltered({
        tipo: TIPOS_EVENTO.TODOS,
        estado: ESTADOS_EVENTO.TODOS,
        fecha_inicio: fechaStr,
        fecha_fin: fechaStr,
      });
      setEventosDia(result.data);
    } catch (err) {
      console.error("Error loading eventos del día:", err);
      toast.error(err?.message || "Error al cargar eventos del día");
      setEventosDia([]);
    } finally {
      setLoadingEventosDia(false);
    }
  }, []);

  // Cargar grupos
  const loadGrupos = useCallback(async () => {
    try {
      const data = await listGrupos();
      setGrupos(data);
    } catch (err) {
      console.error("Error loading grupos:", err);
      toast.error(err?.message || "Error al cargar grupos");
    }
  }, []);

  // Efectos de carga inicial
  useEffect(() => {
    loadEventosCalendario();
    loadGrupos();
  }, [loadEventosCalendario, loadGrupos]);

  useEffect(() => {
    loadEventosDia(selectedDate);
  }, [selectedDate, loadEventosDia]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  function handlePrevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  }

  function handleNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  }

  function handleDayClick(day) {
    const newDate = new Date(currentYear, currentMonth, day);
    setSelectedDate(newDate);
  }

  function openCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function openEdit(evento) {
    setEditing(evento);
    setShowForm(true);
  }

  function askDelete(evento) {
    setTargetDelete(evento);
    setShowDelete(true);
  }

  function closeModal() {
    setShowForm(false);
    setShowDelete(false);
    setEditing(null);
    setTargetDelete(null);
    setSaving(false);
    setDeleting(false);
  }

  // Convocatorias modal handlers
  async function openConvocatoria(evento) {
    if (!evento?.id) return;
    setSelectedEventoConv(evento);
    // ensure all convocatoria accordions are closed when opening modal
    setExpandedConvocatorias({});
    setShowConvocatoria(true);
    setLoadingConvocatorias(true);
    try {
      const data = await getConvocatoriasByEvento(evento.id);
      setConvocatorias(data || []);
      // cargar partidos relacionados (todos para la institución, luego filtramos por convocatoria)
      setLoadingPartidos(true);
      try {
        const all = await getPartidos();
        setPartidos(all || []);
      } catch (err2) {
        console.error("Error cargando partidos:", err2);
        setPartidos([]);
      } finally {
        setLoadingPartidos(false);
      }
    } catch (err) {
      console.error("Error cargando convocatorias:", err);
      toast.error(err?.message || "Error al cargar la convocatoria");
      setConvocatorias([]);
    } finally {
      setLoadingConvocatorias(false);
    }
  }

  function closeConvocatoria() {
    setShowConvocatoria(false);
    setConvocatorias([]);
    setSelectedEventoConv(null);
    setLoadingConvocatorias(false);
    // reset accordion state when closing
    setExpandedConvocatorias({});
    // cerrar cualquier popover abierto dentro del modal
    setPopoverVisibleFor(null);
    setEditingPartido(null);
  }

  function openAddPartidoPopover(convocatoriaId, partido = null) {
    if (partido) {
      setEditingPartido(partido);
      setPartidoForm({
        equipo_local: partido.equipo_local || "",
        equipo_visitante: partido.equipo_visitante || "",
        // sanitize results to only digits for UI (DB may store as text)
        result_local: String(partido.result_local ?? partido.resultLocal ?? "").replace(/\D+/g, ""),
        result_visitante: String(partido.result_visitante ?? partido.resultVisitante ?? "").replace(/\D+/g, ""),
        fecha_partido: partido.fecha_partido ? String(partido.fecha_partido).slice(0,10) : "",
        hora_partido: partido.hora_partido || "",
        cancha: partido.cancha || "",
        estado: partido.estado || "programado",
      });
      setPopoverVisibleFor(convocatoriaId);
      return;
    }

    setEditingPartido(null);
    setPartidoForm({
      equipo_local: "",
      equipo_visitante: "",
      result_local: "",
      result_visitante: "",
      fecha_partido: "",
      hora_partido: "",
      cancha: "",
      estado: "programado",
    });
    setPopoverVisibleFor(convocatoriaId);
  }

  function toggleConvocatoriaExpand(id) {
    setExpandedConvocatorias((s) => ({ ...s, [id]: !s[id] }));
  }

  async function submitPartidoForm(convocatoriaId) {
    const payload = {
      convoc_id: convocatoriaId,
      equipo_local: partidoForm.equipo_local,
      equipo_visitante: partidoForm.equipo_visitante,
      result_local: partidoForm.result_local,
      result_visitante: partidoForm.result_visitante,
      fecha_partido: partidoForm.fecha_partido,
      hora_partido: partidoForm.hora_partido,
      cancha: partidoForm.cancha,
      estado: partidoForm.estado
    };
    // Validación: campos requeridos
    const missing = [];
    if (!String(partidoForm.equipo_local || "").trim()) missing.push("Equipo local");
    if (!String(partidoForm.equipo_visitante || "").trim()) missing.push("Equipo visitante");
    if (!String(partidoForm.fecha_partido || "").trim()) missing.push("Fecha del partido");
    if (!String(partidoForm.hora_partido || "").trim()) missing.push("Hora del partido");
    if (!String(partidoForm.cancha || "").trim()) missing.push("Cancha");
    if (missing.length > 0) {
      toast.error(`Faltan campos requeridos: ${missing.join(", ")}`);
      return;
    }

    try {
      setSavingPartido(true);
      if (editingPartido?.id) {
        await updatePartido(editingPartido.id, payload);
        toast.success("Partido actualizado");
      } else {
        await createPartido(payload);
        toast.success("Partido creado");
      }
      // refrescar partidos
      setLoadingPartidos(true);
      try {
        const all = await getPartidos();
        setPartidos(all || []);
      } finally {
        setLoadingPartidos(false);
      }
      // refrescar convocatorias para el evento actualmente abierto en el modal
      try {
        if (selectedEventoConv?.id) {
          setLoadingConvocatorias(true);
          const fresh = await getConvocatoriasByEvento(selectedEventoConv.id);
          setConvocatorias(fresh || []);
        }
      } catch (errConv) {
        console.error("Error recargando convocatorias:", errConv);
      } finally {
        setLoadingConvocatorias(false);
      }
      setPopoverVisibleFor(null);
      setEditingPartido(null);
    } catch (err) {
      console.error("Error creando/actualizando partido:", err);
      toast.error(err?.message || "Error al guardar partido");
    } finally {
      setSavingPartido(false);
    }
  }

  async function onSubmit(dataForm) {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateEvento(dataForm);
        toast.success("Evento actualizado correctamente");
      } else {
        await createEvento(dataForm);
        toast.success("Evento creado correctamente");
      }
      await loadEventosCalendario();
      await loadEventosDia(selectedDate);
      closeModal();
    } catch (err) {
      console.error("Error saving evento:", err);
      toast.error(err?.message || "Error al guardar evento");
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!targetDelete?.id) return;
    setDeleting(true);
    try {
      await deleteEvento(targetDelete.id);
      toast.success("Evento eliminado correctamente");
      await loadEventosCalendario();
      await loadEventosDia(selectedDate);
      closeModal();
    } catch (err) {
      console.error("Error deleting evento:", err);
      toast.error(err?.message || "Error al eliminar evento");
    } finally {
      setDeleting(false);
    }
  }

  // ============================================================================
  // RENDERIZADO DEL CALENDARIO
  // ============================================================================

  const calendarDays = useMemo(() => {
    const todayDate = new Date();
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];

    // Días vacíos al inicio
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dateStr = formatDate(date);
      
      // Filtrar eventos de este día
      const eventosDelDia = eventosCalendario.filter((evento) => {
        const eventoDate = new Date(evento.fecha_inicio);
        return isSameDay(eventoDate, date);
      });

      days.push({
        day,
        date,
        dateStr,
        eventos: eventosDelDia,
        isToday: isSameDay(date, todayDate),
        isSelected: isSameDay(date, selectedDate),
      });
    }

    return days;
  }, [currentYear, currentMonth, eventosCalendario, selectedDate]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">Eventos</h1>
          <p className="text-slate-600 text-sm">
            Gestión de entrenamientos y torneos deportivos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadEventosCalendario}
            disabled={loadingCalendario}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Recargar datos"
          >
            <ReloadOutlined className="text-base" spin={loadingCalendario} />
          </button>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 text-white px-4 py-2 text-sm font-medium hover:bg-blue-700 active:scale-[.99] transition"
          >
            <PlusIcon className="w-4 h-4" />
            Nuevo evento
          </button>
        </div>
      </div>

      {/* Contenedor principal: Calendario + Lista de eventos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* CALENDARIO */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Header del calendario */}
            <div className="bg-slate-50 border-b border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={handlePrevMonth}
                  className="p-2 rounded-lg hover:bg-slate-200 transition text-slate-700"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <h2 className="text-lg font-semibold text-slate-900">
                  {getMonthName(currentMonth)} {currentYear}
                </h2>
                <button
                  onClick={handleNextMonth}
                  className="p-2 rounded-lg hover:bg-slate-200 transition text-slate-700"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Días de la semana */}
            <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
              {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-semibold text-slate-600 py-2"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Grid de días */}
            <div className="grid grid-cols-7">
              {loadingCalendario ? (
                // Skeleton del calendario
                Array.from({ length: 35 }).map((_, i) => (
                  <div
                    key={i}
                    className="border-b border-r border-slate-200 p-2 h-28"
                  >
                    <div className="h-6 w-6 bg-slate-200 rounded animate-pulse mb-1" />
                    <div className="h-3 w-12 bg-slate-100 rounded animate-pulse" />
                  </div>
                ))
              ) : (
                calendarDays.map((cell, idx) =>
                  cell === null ? (
                    <div
                      key={`empty-${idx}`}
                      className="border-b border-r border-slate-200 bg-slate-50/50 h-28"
                    />
                  ) : (
                    <button
                      key={cell.dateStr}
                      onClick={() => handleDayClick(cell.day)}
                      className={clsx(
                        "border-b border-r border-slate-200 p-2 h-28 text-left transition-all hover:bg-slate-50",
                        cell.isSelected && "bg-blue-50 ring-2 ring-blue-500 ring-inset",
                        cell.isToday && !cell.isSelected && "bg-slate-100"
                      )}
                    >
                      <div
                        className={clsx(
                          "text-sm font-medium mb-1.5 inline-flex items-center justify-center w-6 h-6 rounded-full",
                          cell.isToday && "bg-blue-600 text-white",
                          !cell.isToday && "text-slate-700"
                        )}
                      >
                        {cell.day}
                      </div>
                      <div className="space-y-1">
                        {cell.eventos.slice(0, 2).map((evento) => {
                          const hora = formatTime(evento.fecha_inicio);
                          const esEntrenamiento = evento.tipo === TIPOS_EVENTO.ENTRENAMIENTO;
                          return (
                            <div
                              key={evento.id}
                              className={clsx(
                                "text-[10px] px-1.5 py-0.5 rounded-full border truncate font-medium",
                                esEntrenamiento
                                  ? "bg-blue-50 text-blue-700 border-blue-200"
                                  : "bg-purple-50 text-purple-700 border-purple-200"
                              )}
                              title={`${hora} - ${evento.nombre}`}
                            >
                              {hora} {esEntrenamiento ? "Entrenamiento" : "Torneo"}
                            </div>
                          );
                        })}
                        {cell.eventos.length > 2 && (
                          <div className="text-[10px] text-slate-500 font-medium px-1">
                            +{cell.eventos.length - 2} más
                          </div>
                        )}
                      </div>
                    </button>
                  )
                )
              )}
            </div>
          </div>
        </div>

        {/* LISTA DE EVENTOS DEL DÍA */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden sticky top-6">
            {/* Header */}
            <div className="bg-slate-50 border-b border-slate-200 p-4">
              <h3 className="font-semibold text-base text-slate-900">
                {selectedDate.getDate()} {getMonthName(selectedDate.getMonth())}
              </h3>
              <p className="text-sm text-slate-600">
                {eventosDia.length} evento{eventosDia.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Lista de eventos */}
            <div className="max-h-[600px] overflow-y-auto p-3 space-y-3">
              {loadingEventosDia ? (
                <>
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
                    >
                      <div className="animate-pulse h-40 bg-slate-100" />
                    </div>
                  ))}
                </>
              ) : eventosDia.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                    <InboxIcon className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">
                    No hay eventos programados
                  </h3>
                  <p className="text-xs text-slate-500 text-center">
                    Selecciona otro día o crea un nuevo evento
                  </p>
                </div>
              ) : (
                <>
                  {eventosDia.map((evento) => {
                    const grupo = grupos.find((g) => g.id === evento.grupo_id);
                    return (
                      <article
                        key={evento.id}
                        className="flex flex-col overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-white hover:shadow-md transition"
                      >
                        <div className="p-4">
                          {/* Header con info y botones de acción */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex-1 min-w-0">
                              {/* Hora */}
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-900">
                                  {formatTime(evento.fecha_inicio)} - {formatTime(evento.fecha_fin)}
                                </span>
                                <TipoBadge tipo={evento.tipo} />
                              </div>

                              {/* Nombre */}
                              <h4 className="font-medium text-slate-900 mb-2">
                                {evento.nombre}
                              </h4>
                            </div>
                            
                            {/* Botones de acción en esquina */}
                            <div className="flex gap-1">
                              <button
                                onClick={() => openEdit(evento)}
                                className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                                title="Editar evento"
                                aria-label="Editar"
                              >
                                <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => askDelete(evento)}
                                className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                                title="Eliminar evento"
                                aria-label="Eliminar"
                              >
                                <svg className="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* Información adicional */}
                          <div className="space-y-2 mb-3">
                            {/* Ubicación */}
                            {evento.ubicacion && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{evento.ubicacion}</span>
                              </div>
                            )}

                            {/* Grupo */}
                            {grupo && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                <UsersIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{grupo.nombre}</span>
                              </div>
                            )}
                            {evento.colaborador && evento.tipo === TIPOS_EVENTO.TORNEO && (
                              <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                <UsersIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>
                                  Entrenador: {evento.colaborador?.nombres || ""} {evento.colaborador?.apellidos || ""}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Estado */}
                          <div>
                            <EstadoBadge estado={evento.estado} />
                          </div>
                          {/* Acciones inferiores */}
                          <div className="flex justify-end">
                            {evento.tipo !== TIPOS_EVENTO.ENTRENAMIENTO && (
                              <button
                                onClick={() => openConvocatoria(evento)}
                                className="inline-flex items-center justify-center rounded-md border border-blue-600 text-blue-600 px-2 py-1 text-xs hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 active:scale-[.99] transition mt-3"
                                title="Convocatoria"
                                aria-label="Convocatoria"
                              >
                                Convocatoria
                              </button>
                            )}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Crear/Editar */}
      <ModalEvento
        open={showForm}
        evento={editing}
        grupos={grupos}
        saving={saving}
        onCancel={closeModal}
        onSave={onSubmit}
      />

      {/* Modal Eliminar */}
      <Modal
        open={showDelete}
        onCancel={closeModal}
        title="Eliminar evento"
        footer={null}
        centered
      >
        <p className="text-sm text-slate-600 mb-4">
          ¿Seguro que deseas eliminar el evento{" "}
          <span className="font-medium text-slate-900">
            {targetDelete?.nombre}
          </span>
          ? Esta acción no se puede deshacer.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={closeModal}
            disabled={deleting}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleting}
            className="rounded-xl bg-rose-600 text-white px-4 py-2 text-sm hover:bg-rose-700 disabled:opacity-60 transition"
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </button>
        </div>
      </Modal>

      {/* Modal Convocatoria */}
      <Modal
        open={showConvocatoria}
        onCancel={closeConvocatoria}
        title={`Convocatoria - ${selectedEventoConv?.nombre || selectedEventoConv?.id || ""}`}
        footer={null}
        centered
      >
        {loadingConvocatorias ? (
          <div className="space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="p-3 rounded-xl border border-slate-200 bg-white">
                <div className="animate-pulse">
                  <div className="h-7 bg-slate-200 rounded w-1/3 mb-3" />
                  <div className="h-7 bg-slate-100 rounded w-1/2 mb-2" />
                </div>
              </div>
            ))}
          </div>
        ) : convocatorias.length === 0 ? (
          <div className="py-6 text-center">
            <p className="text-sm text-slate-600">No hay convocatorias para este evento.</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {convocatorias.map((c) => {
              // prefer nested partidos from the convocatoria payload, otherwise filtrar global
              const related = (c.partidos && c.partidos.length)
                ? c.partidos
                : partidos.filter((p) =>
                    p?.convocatoria_id === c.id || p?.convocatoriaId === c.id || p?.convocatoria === c.id
                  );

              const isFormValid = Boolean(
                String(partidoForm.equipo_local || "").trim() &&
                String(partidoForm.equipo_visitante || "").trim() &&
                String(partidoForm.fecha_partido || "").trim() &&
                String(partidoForm.hora_partido || "").trim() &&
                String(partidoForm.cancha || "").trim()
              );

              const formContent = (
                <div className="w-[320px]">
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                        placeholder="Equipo local"
                        value={partidoForm.equipo_local}
                        onChange={(e) => setPartidoForm((s) => ({ ...s, equipo_local: e.target.value }))}
                        disabled={savingPartido}
                      />
                      <input
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                        placeholder="Equipo visitante"
                        value={partidoForm.equipo_visitante}
                        onChange={(e) => setPartidoForm((s) => ({ ...s, equipo_visitante: e.target.value }))}
                        disabled={savingPartido}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\\d*"
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                        placeholder="Resultado local"
                        value={partidoForm.result_local}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D+/g, "");
                          setPartidoForm((s) => ({ ...s, result_local: onlyDigits }));
                        }}
                        onPaste={(e) => {
                          try {
                            const paste = (e.clipboardData || window.clipboardData).getData('text');
                            if (paste && /\D/.test(paste)) {
                              e.preventDefault();
                              const digits = paste.replace(/\D+/g, '');
                              setPartidoForm((s) => ({ ...s, result_local: digits }));
                            }
                          } catch (err) {
                            // ignore paste sanitization errors
                          }
                        }}
                        disabled={savingPartido}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\\d*"
                        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                        placeholder="Resultado visitante"
                        value={partidoForm.result_visitante}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D+/g, "");
                          setPartidoForm((s) => ({ ...s, result_visitante: onlyDigits }));
                        }}
                        onPaste={(e) => {
                          try {
                            const paste = (e.clipboardData || window.clipboardData).getData('text');
                            if (paste && /\D/.test(paste)) {
                              e.preventDefault();
                              const digits = paste.replace(/\D+/g, '');
                              setPartidoForm((s) => ({ ...s, result_visitante: digits }));
                            }
                          } catch (err) {
                            // ignore paste sanitization errors
                          }
                        }}
                        disabled={savingPartido}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                        value={partidoForm.fecha_partido}
                        onChange={(e) => setPartidoForm((s) => ({ ...s, fecha_partido: e.target.value }))}
                        disabled={savingPartido}
                      />
                      <input
                        type="time"
                        className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                        value={partidoForm.hora_partido}
                        onChange={(e) => setPartidoForm((s) => ({ ...s, hora_partido: e.target.value }))}
                        disabled={savingPartido}
                      />
                    </div>
                    <input
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                      placeholder="Lugar"
                      value={partidoForm.cancha}
                      onChange={(e) => setPartidoForm((s) => ({ ...s, cancha: e.target.value }))}
                      disabled={savingPartido}
                    />
                    <select
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
                      value={partidoForm.estado}
                      onChange={(e) => setPartidoForm((s) => ({ ...s, estado: e.target.value }))}
                      disabled={savingPartido}
                    >
                      <option value="programado">programado</option>
                      <option value="finalizado">finalizado</option>
                      <option value="cancelado">cancelado</option>
                    </select>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setPopoverVisibleFor(null); setEditingPartido(null); }}
                        className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60"
                        type="button"
                        disabled={savingPartido}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => submitPartidoForm(c.id)}
                        className="rounded-xl bg-blue-600 text-white px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-60 transition disabled:cursor-not-allowed flex items-center gap-2"
                        type="button"
                        disabled={!isFormValid || savingPartido}
                      >
                        {savingPartido && (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                        )}
                        {editingPartido ? (savingPartido ? "Guardando..." : "Guardar") : (savingPartido ? "Guardando..." : "Guardar")}
                      </button>
                    </div>
                  </div>
                </div>
              );

              return (
                <div key={c.id || JSON.stringify(c)} className="p-3 rounded-xl border border-slate-200 bg-white">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleConvocatoriaExpand(c.id)}
                          className="rounded-md p-1 hover:bg-slate-100 transition"
                          aria-label={expandedConvocatorias[c.id] ? "Ocultar partidos" : "Mostrar partidos"}
                        >
                          <svg className={clsx("w-4 h-4 text-slate-600 transition-transform", expandedConvocatorias[c.id] && "rotate-90")} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                          </svg>
                        </button>
                        <div className="truncate">{c.nombre || (c.deportista?.nombres ? `${c.deportista.nombres} ${c.deportista.apellidos || ""}` : `ID: ${c.id}`)}</div>
                      </div>
                      <div className="text-xs text-slate-500">
                        {c.fecha_convocatoria || c.fecha || c.fecha_inicio || ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.estado ? <Badge variant="info">{c.estado}</Badge> : null}
                    </div>
                  </div>

                  {/* Partidos relacionados - accordion */}
                  {expandedConvocatorias[c.id] ? (
                    <div className="mt-3 space-y-2">
                      {loadingPartidos ? (
                        <div className="text-sm text-slate-600">Cargando partidos...</div>
                      ) : related.length === 0 ? (
                        <div className="text-sm text-slate-500">No hay partidos.</div>
                      ) : (
                        related.map((p) => (
                          <div key={p.id || JSON.stringify(p)} className="flex items-center justify-between gap-3 p-2 rounded-md border border-slate-100 bg-slate-50">
                            <div className="min-w-0 text-sm">
                              <div className="font-medium truncate">
                                {p.equipo_local || "-"} {typeof p.result_local !== 'undefined' && p.result_local !== null ? p.result_local : "-"} - {typeof p.result_visitante !== 'undefined' && p.result_visitante !== null ? p.result_visitante : "-"} {p.equipo_visitante || "-"}
                              </div>
                              <div className="text-xs text-slate-500">{p.fecha_partido || p.fecha} {p.hora_partido || p.hora} {p.cancha ? `· ${p.cancha}` : ""}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openAddPartidoPopover(c.id, p)}
                                className="rounded-lg border border-slate-200 hover:bg-slate-100 p-2 transition"
                                title="Editar partido"
                                aria-label="Editar partido"
                              >
                                <svg className="w-4 h-4 text-slate-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 text-sm text-slate-500">
                      {related.length === 0 ? (
                        "No hay partidos."
                      ) : (
                        <button type="button" onClick={() => toggleConvocatoriaExpand(c.id)} className="rounded-md px-3 py-1 text-sm border border-slate-200 hover:bg-slate-50">Ver partidos ({related.length})</button>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end mt-3">
                    <Popover
                      content={formContent}
                      title={editingPartido ? "Editar partido" : "Agregar partido"}
                      trigger="click"
                      visible={popoverVisibleFor === c.id}
                      onVisibleChange={(v) => { if (!v) { setPopoverVisibleFor(null); setEditingPartido(null); } }}
                    >
                      <button
                        onClick={() => openAddPartidoPopover(c.id)}
                        className="inline-flex items-center justify-center rounded-md border border-blue-600 text-blue-600 px-2 py-1 text-xs hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 active:scale-[.99] transition"
                        aria-label="Agregar partido"
                      >
                        <svg className="w-3 h-3 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Agregar partido
                      </button>
                    </Popover>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}
